import imageCompression from "browser-image-compression";
import { STATUS_IDS, USER_ROLES_IDS } from "../constants/common";
import jsPDF from "jspdf";
import { APP_ROUTES } from '../routes';

export const formatLabel = (label: string) => {
  return label
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (str) => str.toUpperCase());
};

const getFileExtension = (mimeType: string): string => {
  const mimeMap: Record<string, string> = {
    "image/png": "png",
    "image/jpeg": "jpeg",
    "image/jpg": "jpg",
    "image/gif": "gif",
    "application/pdf": "pdf",
    "application/octet-stream": "bin",
  };

  return mimeMap[mimeType] || "bin"; // Default to .bin for unknown types
};

export const hexToFile = (
  hexString: string,
  mimeType: string = "application/octet-stream",
): File => {
  // Convert hex string to binary
  const binaryData = Uint8Array.from(
    hexString.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) || [],
  );

  // Create a Blob from binary data
  const blob = new Blob([binaryData], { type: mimeType });

  // Derive file extension from MIME type
  const fileExtension = getFileExtension(mimeType);

  // Create and return a File object
  const fileName = `file.${fileExtension}`;
  return new File([blob], fileName, { type: mimeType });
};

export const handleFileChange = async (
  event: React.ChangeEvent<HTMLInputElement>,
): Promise<string | undefined> => {
  const file = event.target.files?.[0];

  if (file) {
    try {
      const compressedFile = await compressImage(file);
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsArrayBuffer(compressedFile);

        reader.onload = () => {
          if (reader.result) {
            const arrayBuffer = reader.result as ArrayBuffer;
            // const hexString = arrayBufferToHex(arrayBuffer); // Convert to hex
            const hexString = arrayBufferToBytes(arrayBuffer);
            resolve(hexString);
          } else {
            resolve(undefined);
          }
        };

        reader.onerror = (error) => {
          console.error("Error reading file:", error);
          reject(error);
        };
      });
    } catch (error) {
      console.error("Error compressing image:", error);
      return undefined;
    }
  }

  return undefined;
};

export const compressImage = async (file: File): Promise<File> => {
  const options = {
    maxSizeMB: 1, // Maximum file size (in MB) after compression
    maxWidthOrHeight: 1920, // Maximum width or height
    useWebWorker: true, // Use web workers for faster compression
  };

  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    console.error("Error during image compression:", error);
    throw error;
  }
};

const arrayBufferToBytes = (buffer: ArrayBuffer): string => {
  const byteArray = new Uint8Array(buffer);
  return Array.from(byteArray)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join(" ");
};

export const mapToOptions = <T>(
  labelEnKey: keyof T,
  labelArKey: keyof T,
  valueKey: keyof T,
  data?: T[],
) => {
  return (
    data?.map((item) => ({
      labelEn: item[labelEnKey] || "",
      labelAr: item[labelArKey] || "",
      value: item[valueKey] || "",
    })) || []
  );
};

export const mapToMultipleOptions = <T>(
  labelKey: keyof T,
  detailKey: keyof T,
  detail2Key: keyof T,
  valueKey: keyof T,
  data?: T[],
) => {
  return (
    data?.map((item) => ({
      label: item[labelKey],
      detail: item[detailKey],
      detail2: item[detail2Key],
      value: item[valueKey],
    })) || []
  );
};

export const globalFilterFn = (
  row: { original: { [key: string]: any } },
  _: string,
  filterValue: string,
) => {
  const searchValue = filterValue.toLowerCase();

  return Object.values(row.original).some((value) => {
    if (value) {
      return value.toString().toLowerCase().includes(searchValue);
    }
    return false;
  });
};

export const formattedDate = (date: Date, isFormatSlash = false) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Add leading zero to month
  const day = String(date.getDate()).padStart(2, "0"); // Add leading zero to day

  if (!isFormatSlash) {
    return `${year}-${month}-${day}`;
  } else {
    return `${month}/${day}/${year}`;
  }
};

const pad = (num: number) => String(num).padStart(2, "0");

export const formattedDateTime = (date: Date) => {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds(),
  )}`;
};

export const formattedDateTimeWithoutSec = (date: string) => {
  if (!date) return "";

  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12; // convert 0 -> 12 for AM/PM
  const formattedHours = String(hours).padStart(2, "0");

  const result = `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;

  // Wrap with LTR marker to prevent RTL flipping
  return `\u200E${result}`;
};

export const getDateOneMonthAgo = () => {
  const currentDate = new Date();
  const oneMonthAgo = new Date(currentDate);
  oneMonthAgo.setMonth(currentDate.getMonth() - 1);

  return oneMonthAgo.toISOString().split("T")[0]; // Format the date to YYYY-MM-DD format
};

export const getDateOneWeekAgo = () => {
  const currentDate = new Date();
  const oneWeekAgo = new Date(currentDate);
  oneWeekAgo.setDate(currentDate.getDate() - 6); // Subtract 6 days

  return oneWeekAgo.toISOString().split("T")[0]; // Format the date to YYYY-MM-DD
};

export const calculateTicks = (
  min: number,
  max: number,
  step: number,
): number[] => {
  const ticks = [];
  for (let i = min; i <= max; i += step) {
    ticks.push(i);
  }
  return ticks;
};

export const calculateDomain = (values: number[]): [number, number] => {
  if (!values) {
    return [0, 0];
  }

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const roundedMin = Math.floor(minValue);
  const roundedMax = Math.ceil(maxValue);
  return [roundedMin, roundedMax];
};

export const getRequestInfoStatusBgColor = (item: string) => {
  switch (item) {
    case STATUS_IDS.REQUEST_INFO.toString():
      return "bg-yellow-500";
    case STATUS_IDS.UN_SELECTED.toString():
      return "bg-yellow-500";
    case STATUS_IDS.SELECT.toString():
      return "bg-gray-500";
    case STATUS_IDS.APPROVED.toString():
      return "bg-green-500";
    case STATUS_IDS.SELECTED.toString():
      return "bg-green-500";
    default:
      return "";
  }
};

export const isExpired = (date: Date) => {
  const today = new Date();

  // Set both dates to midnight to compare only the date parts
  date.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return date < today;
};

export function onlyDate(date: string | Date) {
  let d;

  if (typeof date === "string") {
    d = new Date(date);
  } else {
    d = date;
  }
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function base64ToFile(
  base64String: string,
  filename: string,
  mimeType: string,
): File {
  const byteString = atob(
    base64String.includes(",") ? base64String.split(",")[1] : base64String,
  );
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new File([ab], filename, { type: mimeType });
}

export function isVisitWithinVisitingHours(
  visitStartDateStr: string,
  visitEndDateStr: string,
  visitingHours: {
    visitorsStartTime: string;
    visitorsEndTime: string;
  },
): boolean {
  function parseTimeToMinutes(timeStr: string): number {
    const [time, modifier] = timeStr.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (modifier === "PM" && hours !== 12) hours += 12;
    if (modifier === "AM" && hours === 12) hours = 0;

    return hours * 60 + minutes;
  }

  function extractTimeInMinutes(dateStr: string): number {
    const date = new Date(dateStr);
    return date.getHours() * 60 + date.getMinutes();
  }

  const startMinutes = extractTimeInMinutes(visitStartDateStr);
  const endMinutes = extractTimeInMinutes(visitEndDateStr);
  const allowedStart = parseTimeToMinutes(visitingHours.visitorsStartTime);
  const allowedEnd = parseTimeToMinutes(visitingHours.visitorsEndTime);

  // Check if BOTH start and end are within allowed time range
  const isStartWithin =
    startMinutes >= allowedStart && startMinutes <= allowedEnd;
  const isEndWithin = endMinutes >= allowedStart && endMinutes <= allowedEnd;

  return isStartWithin && isEndWithin;
}

export function convertTimeToISOString(timeStr: string): string {
  const date = new Date();
  const [rawHour, rest] = timeStr.split(":");
  const [minutePart, ampm] = rest.split(" ");
  let hours = parseInt(rawHour);
  const minutes = parseInt(minutePart);

  if (ampm.toLowerCase() === "pm" && hours < 12) hours += 12;
  if (ampm.toLowerCase() === "am" && hours === 12) hours = 0;

  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
}

export function formatToTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = () => {
      if (typeof reader.result === "string") {
        const base64String = reader.result.split(",")[1];
        resolve(base64String);
      } else {
        reject(new Error("FileReader result is not a string."));
      }
    };

    reader.onerror = (error) => reject(error);
  });
}

export function formatJsonData(log: string) {
  const entries = log.split(/\r?\n/).filter((line) => line.trim().length > 0);

  const formattedLogs = entries.map((line) => {
    const isAlreadyFormatted =
      /^\[\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}\]/.test(line);
    if (isAlreadyFormatted) return line;

    try {
      const entry: Record<string, any> = JSON.parse(line);
      const timestamp = new Date(entry["@t"])
        .toLocaleString("en-GB", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
        .replace(",", "");

      let message = entry["@mt"];

      // Replace placeholders dynamically
      message = message.replace(/{([^}]+)}/g, (_: any, key: number) => {
        const value = entry[key];
        if (typeof value === "number") {
          return value.toFixed(4); // or just `value` if you don’t want decimals
        }
        return value ?? `{${key}}`; // keep original if not found
      });

      return `[${timestamp}] ${message}`;
    } catch (error) {
      console.error("Invalid log format:", line);
      return "Invalid log format";
    }
  });

  return formattedLogs.join("\n");
}

export function downloadStringAsPDF(
  content: string,
  fileName: string = "output.pdf",
) {
  const doc = new jsPDF();

  doc.setFontSize(12);

  // Split text into lines that fit the page width
  const pageWidth = doc.internal.pageSize.getWidth();
  const lines = doc.splitTextToSize(content, pageWidth - 20); // 10pt margin each side

  doc.text(lines, 10, 10); // Add text to the PDF at (x=10, y=10)
  doc.save(fileName); // Trigger download
}

export const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

export function blobToObjectURL(blob: Blob): string {
  return URL.createObjectURL(blob);
}

export function isCheckPermitApprovedByNextApprover(
  pathname: string,
  currentPathname: string,
  currentApproverOrderNo?: number
): boolean {
  return !!(
    pathname === currentPathname &&
    currentApproverOrderNo &&
    currentApproverOrderNo > 1
  )
}

export function isPermitFieldsDisabled(
  permit: { n_ApprovalStatus: number },
  isPermitApprovedByNextApprover: boolean,
  pathname: string,
  currentPathname: string,
  userRoleId?: number,
): boolean {
  return (userRoleId !== USER_ROLES_IDS.STAFF && userRoleId !== USER_ROLES_IDS.ADMIN) ||
    (permit && permit?.n_ApprovalStatus !== STATUS_IDS.PENDING) ||
    (userRoleId === USER_ROLES_IDS.STAFF && isPermitApprovedByNextApprover) ||
    (userRoleId === USER_ROLES_IDS.ADMIN && pathname === currentPathname)
}